import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ErrorBoundary } from './components/ErrorBoundary';
import { BUILD_INFO } from './lib/buildInfo';
import { useStore } from './store/useStore';
import { useEffect, Suspense, lazy } from 'react';
import { THEMES } from './lib/themes';
import { ConfirmDialog } from './components/ui/ConfirmDialog';
import { PromptDialog } from './components/ui/PromptDialog';
import { ToastContainer } from './components/ui/Toast';

// Lazy load pages for better performance
const Login = lazy(() => import('./pages/Login'));
// const SignUp = lazy(() => import('./pages/SignUp')); // Removed in favor of LandingPlans
const LandingPlans = lazy(() => import('./pages/LandingPlans'));
const RegisterTenant = lazy(() => import('./pages/RegisterTenant'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const AboutUs = lazy(() => import('./pages/AboutUs'));

// Eager load core pages to prevent white flash
import Dashboard from './pages/Dashboard';
import Sales from './pages/Sales';

const History = lazy(() => import('./pages/History'));
const Inventory = lazy(() => import('./pages/Inventory'));
const Purchases = lazy(() => import('./pages/Purchases'));
const Users = lazy(() => import('./pages/Users'));
const Roles = lazy(() => import('./pages/Roles'));
const Reports = lazy(() => import('./pages/Reports'));
const Settings = lazy(() => import('./pages/Settings'));
const Clients = lazy(() => import('./pages/Clients'));
const Expenses = lazy(() => import('./pages/Expenses'));
const Loyalty = lazy(() => import('./pages/Loyalty'));
const CashFlow = lazy(() => import('./pages/CashFlow'));
const Subscription = lazy(() => import('./pages/Subscription'));
const SubscriptionPlans = lazy(() => import('./pages/SubscriptionPlans'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const CheckoutStatus = lazy(() => import('./pages/CheckoutStatus'));
const Leads = lazy(() => import('./pages/Leads'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions'));
const Demo = lazy(() => import('./pages/Demo'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const UpdatePassword = lazy(() => import('./pages/UpdatePassword'));

// Loading component
const LoadingSpinner = () => (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
);

function AppContent() {
    const checkSession = useStore(state => state.checkSession);
    const settings = useStore(state => state.settings);
    const sessionLoading = useStore(state => state.sessionLoading);
    const user = useStore(state => state.user);

    useEffect(() => {
        console.log('🚀 InnovaClean SaaS - Build Info:', BUILD_INFO);
        console.log('Checking session...');
        checkSession();
    }, [checkSession]);

    useEffect(() => {
        // Dynamic Title Logic - DISABLED per user request to enforce global branding
        // if (user && settings.companyName) {
        //     document.title = `${settings.companyName} - Panel`;
        // } else if (location.pathname.startsWith('/dashboard') || location.pathname.startsWith('/admin')) {
        //     document.title = 'Innova Admin';
        // } else {
        //     document.title = 'Inventory App SaaS';
        // }

        // Favicon Logic - DISABLED
        // if (settings.logo) {
        //     let link: HTMLLinkElement | null = document.querySelector("link[rel*='icon']");
        //     if (!link) {
        //         link = document.createElement('link');
        //         link.rel = 'shortcut icon';
        //         document.head.appendChild(link);
        //     }
        //     link.href = settings.logo;
        // }

        // Apply Theme
        const theme = THEMES.find(t => t.id === settings.themeId) || THEMES[0];
        const root = document.documentElement;
        Object.entries(theme.colors).forEach(([shade, value]) => {
            root.style.setProperty(`--color-primary-${shade}`, value);
        });

    }, [settings.themeId]);

    if (sessionLoading) {
        return <LoadingSpinner />;
    }

    return (
        <Suspense fallback={<LoadingSpinner />}>
            <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<LandingPlans />} />
                <Route path="/register" element={<RegisterTenant />} />
                <Route path="/about-us" element={<AboutUs />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsAndConditions />} />
                <Route path="/demo" element={<Demo />} />

                {/* Onboarding is protected but allows users without tenantId */}
                <Route path="/onboarding" element={
                    user ? <Onboarding /> : <Navigate to="/login" replace />
                } />

                <Route path="/dashboard" element={
                    <ProtectedRoute requiredPermission="reports:view">
                        <ErrorBoundary>
                            <Dashboard />
                        </ErrorBoundary>
                    </ProtectedRoute>
                } />

                <Route path="/purchases" element={
                    <ProtectedRoute requiredPermission="products:manage">
                        <Purchases />
                    </ProtectedRoute>
                } />

                <Route path="/users" element={
                    <ProtectedRoute requiredPermission="users:manage">
                        <Users />
                    </ProtectedRoute>
                } />

                <Route path="/roles" element={
                    <ProtectedRoute requiredPermission="users:manage">
                        <Roles />
                    </ProtectedRoute>
                } />

                <Route path="/reports" element={
                    <ProtectedRoute requiredPermission="reports:view">
                        <Reports />
                    </ProtectedRoute>
                } />

                <Route path="/clients" element={
                    <ProtectedRoute requiredPermission="clients:read">
                        <Clients />
                    </ProtectedRoute>
                } />

                <Route path="/settings" element={
                    <ProtectedRoute requiredPermission="settings:manage">
                        <Settings />
                    </ProtectedRoute>
                } />

                <Route path="/sales" element={
                    <ProtectedRoute requiredPermission="sales:create">
                        <ErrorBoundary>
                            <Sales />
                        </ErrorBoundary>
                    </ProtectedRoute>
                } />

                <Route path="/history" element={
                    <ProtectedRoute requiredPermission="sales:read">
                        <History />
                    </ProtectedRoute>
                } />

                <Route path="/inventory" element={
                    <ProtectedRoute requiredPermission="products:read">
                        <Inventory />
                    </ProtectedRoute>
                } />

                <Route path="/expenses" element={
                    <ProtectedRoute requiredPermission="expenses:manage">
                        <Expenses />
                    </ProtectedRoute>
                } />

                <Route path="/loyalty" element={
                    <ProtectedRoute requiredPermission="clients:read">
                        <Loyalty />
                    </ProtectedRoute>
                } />

                <Route path="/cash-flow" element={
                    <ProtectedRoute requiredPermission="cashflow:read">
                        <CashFlow />
                    </ProtectedRoute>
                } />

                <Route path="/subscription" element={
                    <ProtectedRoute>
                        <Subscription />
                    </ProtectedRoute>
                } />

                <Route path="/subscription-plans" element={
                    <ProtectedRoute allowedRoles={['superadmin']}>
                        <ErrorBoundary>
                            <SubscriptionPlans />
                        </ErrorBoundary>
                    </ProtectedRoute>
                } />

                <Route path="/pricing" element={
                    <ProtectedRoute>
                        <PricingPage />
                    </ProtectedRoute>
                } />

                <Route path="/checkout/status" element={
                    <ProtectedRoute>
                        <CheckoutStatus />
                    </ProtectedRoute>
                } />

                <Route path="/leads" element={
                    <ProtectedRoute allowedRoles={['superadmin']}>
                        <ErrorBoundary>
                            <Leads />
                        </ErrorBoundary>
                    </ProtectedRoute>
                } />

                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/update-password" element={<UpdatePassword />} />

                <Route path="*" element={<Navigate to="/signup" replace />} />
            </Routes>
        </Suspense>
    );
}

function App() {
    return (
        <BrowserRouter>
            <AppContent />
            <ConfirmDialog />
            <PromptDialog />
            <ToastContainer />
        </BrowserRouter>
    );
}

export default App;
